import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import config from '../../../config/app';
import checkRole from '../../utils/checkRole';
import roles from '../../pages/customers/roles/roles';



const HtmlEditor = ({ initialValue, onChange, init }) => {
  const editorRef = useRef(null);
  const { userRoles, users } = useSelector(
    ({ user, users }) => ({
      users,
      userRoles: user.roles,
    }),
    shallowEqual,
  );

  const mentionsAllowed = checkRole(userRoles, [
    roles['lead-coloring-editor'].key,
    roles['coloring-editor'].key,
    roles['remote-coloring-editor'].key,
    roles['content-manager'].key,
    roles['coloring-tester'].key,
  ]);

  const setup = (editor) => {
    const onAction = (autocompleteApi, rng, value) => {
      editor.selection.setRng(rng);
      editor.insertContent(value);
      autocompleteApi.hide();

      const lastSpan = editor.dom.select('span[data-value]').pop();

      if (lastSpan) {
        // Создаем текстовый узел (неразрывный пробел для корректного отображения)
        const textNode = document.createTextNode('\u00A0'); // Используем неразрывный пробел

        // Вставляем текстовый узел после последнего <span>
        lastSpan.parentNode.insertBefore(textNode, lastSpan.nextSibling);

        // Устанавливаем курсор после текстового узла
        const range = editor.dom.createRng();

        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        editor.selection.setRng(range);
      }
    };

    const getMatchedChars = (pattern, editor) => {
      const currentText = editor.getContent({ format: 'text' });

      return users?.filter((user) => !user.inactive
        && !user.roles.includes(roles.administrator.key)
        && !user.roles.includes(roles['coloring-artist'].key)
        && !user.roles.includes(roles['coloring-designer'].key)
        && user.username.toLowerCase().indexOf(pattern?.toLowerCase()) !== -1
        && !currentText.includes(user.username));
    };

    if (mentionsAllowed) {
      editor.ui.registry.addAutocompleter('users', {
        trigger: '@',
        minChars: 1,
        columns: 1,
        maxResults: 10,
        highlightOn: [ 'username' ],
        onAction,
        fetch: (pattern, maxResults) => {
          return new Promise((resolve) => {
            const results = getMatchedChars(pattern, editor).map((user) => ({
              type: 'cardmenuitem',
              value: `<span data-value="${user.username}"><b>@${user.username}</b></span>`,
              label: user.username,
              items: [
                {
                  type: 'cardcontainer',
                  direction: 'vertical',
                  items: [
                    {
                      type: 'cardtext',
                      text: user.username,
                      name: 'username',
                    },
                  ],
                },
              ],
            }));

            resolve(results?.slice(0, maxResults));
          });
        },
      });
    }
  };

  return (
    <Editor
      tinymceScriptSrc='../../js/tinymce/tinymce.min.js'
      onInit={(evt, editor) => {
        editorRef.current = editor;
      }}
      value={initialValue}
      onEditorChange={onChange}
      init={{
        ...init,
        setup,
      }}
    />
  );
};

HtmlEditor.propTypes = {
  initialValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  init: PropTypes.object,
};

HtmlEditor.defaultProps = {
  initialValue: '',
  init: {
    license_key: config.tinyEditorApiKey,
    height: '10vh',
    plugins: 'print preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media template codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount imagetools textpattern help charmap quickbars emoticons',
    imagetools_cors_hosts: [ 'picsum.photos' ],
    menubar: 'file edit view insert format tools table help',
    toolbar: 'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media template link anchor codesample | ltr rtl',
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
  },
};


export default HtmlEditor;
